'use client';

import React from 'react';
import { getAiSearchStatus } from '@repo/api';

export type AiSearchStatus = 'loading' | 'enabled' | 'disabled';

// `/ai-search/status/` is identical for every caller and edge-cached with a ~30s
// TTL. Mirror that on the client with a tiny module-level cache so the desktop +
// mobile bars share a single request and don't refetch on every mount.
const TTL_MS = 30_000;
let cached: { enabled: boolean; at: number } | null = null;
let inflight: Promise<boolean> | null = null;

function isFresh(): boolean {
  return cached !== null && Date.now() - cached.at < TTL_MS;
}

async function fetchStatus(): Promise<boolean> {
  if (isFresh()) return cached!.enabled;
  if (inflight) return inflight;
  inflight = getAiSearchStatus()
    .then(res => {
      cached = { enabled: res.enabled === true, at: Date.now() };
      return cached.enabled;
    })
    .catch(() => {
      // If the switch can't be read, fall back to the legacy filter UI — safer
      // than rendering an AI bar that may be broken. Cache the miss briefly so we
      // don't hammer a failing endpoint.
      cached = { enabled: false, at: Date.now() };
      return false;
    })
    .finally(() => {
      inflight = null;
    });
  return inflight;
}

/**
 * Reads the AI-search feature switch. Returns `'loading'` until resolved, then
 * `'enabled'` / `'disabled'`. Callers render the AI bar only for `'enabled'` and
 * the legacy filter UI otherwise.
 */
export function useAiSearchStatus(): AiSearchStatus {
  const [status, setStatus] = React.useState<AiSearchStatus>(() =>
    isFresh() ? (cached!.enabled ? 'enabled' : 'disabled') : 'loading',
  );

  React.useEffect(() => {
    let active = true;
    if (isFresh()) {
      setStatus(cached!.enabled ? 'enabled' : 'disabled');
      return;
    }
    void fetchStatus().then(enabled => {
      if (active) setStatus(enabled ? 'enabled' : 'disabled');
    });
    return () => {
      active = false;
    };
  }, []);

  return status;
}

export default useAiSearchStatus;
